import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Download,
  Eye,
  FileText,
  Image as ImageIcon,
  Info,
  Loader2,
  MessageCircle,
  Paperclip,
  PanelLeftClose,
  PanelLeftOpen,
  Pin,
  Search,
  Send,
  X,
} from "lucide-react";
import {
  getRrhhChatMessages,
  getRrhhChatUsers,
  markRrhhChatAsRead,
  sendRrhhChatMessage,
  downloadRrhhChatAttachment,
  loadRrhhChatAttachmentPreview,
  type RrhhChatAttachment,
  type RrhhChatMessage,
  type RrhhChatUser,
} from "../api/rrhh-chat";
import { ChatImagePreviewModal } from "@shared/components/ChatImagePreviewModal";
import { GroupChatWorkspace } from "@shared/components/GroupChat/GroupChatWorkspace";
import { useGroupChatHub } from "@shared/components/GroupChat/ChatHub";

const quickReplies = [
  "Necesito ayuda con el fichaje",
  "Tengo una duda sobre vacaciones",
  "No puedo descargar mi nómina",
  "Necesito un documento del contrato",
  "¿Me podéis revisar esta solicitud?",
  "Gracias",
];

const pinnedReplies = [
  "Revisar solicitud",
  "Duda nómina",
  "Fichaje",
  "Documentación",
];

const RRHH_PENDING_MESSAGES_KEY = "rrhh-chat-pending-messages-v1";

type PendingRrhhMessage = RrhhChatMessage & {
  admin_id: number;
};

type Props = {
  floating?: boolean;
};

export function RrhhChat({ floating = false }: Props) {
  const {
    groups,
    loadingGroups,
    groupsError,
    selectedGroupId,
    selectGroup,
  } = useGroupChatHub();
  const [users, setUsers] = useState<RrhhChatUser[]>([]);
  const [messages, setMessages] = useState<RrhhChatMessage[]>([]);
  const [selectedUser, setSelectedUser] = useState<RrhhChatUser | null>(null);
  const [showPinned, setShowPinned] = useState(false);
  const [floatingConversationsExpanded, setFloatingConversationsExpanded] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [chatSearch, setChatSearch] = useState("");
  const [message, setMessage] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [previewAttachment, setPreviewAttachment] = useState<RrhhChatAttachment | null>(null);
  const [loadingChatId, setLoadingChatId] = useState<number | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(true);

  const selectedUserIdRef = useRef<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const messagesCacheRef = useRef<Record<number, RrhhChatMessage[]>>({});
  const loadingMessagesRef = useRef<Record<number, boolean>>({});
  const pendingMessagesRef = useRef<Record<string, PendingRrhhMessage>>({});
  const sendingIdsRef = useRef<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const nextClientOrder = () => Date.now() + Math.random();

  const sortChatMessages = (items: RrhhChatMessage[]) =>
    [...items].sort((a, b) => {
      const aOrder = a.client_order ?? new Date(a.creado_en).getTime();
      const bOrder = b.client_order ?? new Date(b.creado_en).getTime();

      if (aOrder !== bOrder) return aOrder - bOrder;

      return a.id - b.id;
    });

  const persistPendingMessages = () => {
    localStorage.setItem(
      RRHH_PENDING_MESSAGES_KEY,
      JSON.stringify(Object.values(pendingMessagesRef.current)),
    );
  };

  const addMessageToCache = (adminId: number, msg: RrhhChatMessage) => {
    const current = messagesCacheRef.current[adminId] ?? [];

    if (msg.client_id && current.some((m) => m.client_id === msg.client_id)) {
      return;
    }

    if (msg.id > 0 && current.some((m) => m.id === msg.id)) {
      return;
    }

    const next = sortChatMessages([...current, msg]);
    messagesCacheRef.current[adminId] = next;

    if (selectedUserIdRef.current === adminId) {
      setMessages(next);
    }
  };

  const replacePendingInCache = (
    adminId: number,
    clientId: string,
    stored: RrhhChatMessage,
  ) => {
    const current = messagesCacheRef.current[adminId] ?? [];

    const next = sortChatMessages(
      current
        .map((msg) =>
          msg.client_id === clientId
            ? {
                ...stored,
                client_order: msg.client_order,
              }
            : msg,
        )
        .filter(
          (msg, index, arr) =>
            msg.id < 0 || arr.findIndex((item) => item.id === msg.id) === index,
        ),
    );

    messagesCacheRef.current[adminId] = next;

    if (selectedUserIdRef.current === adminId) {
      setMessages(next);
    }
  };

  const markPendingError = (adminId: number, clientId: string) => {
    const current = messagesCacheRef.current[adminId] ?? [];

    const next = current.map((msg) =>
      msg.client_id === clientId ? { ...msg, status: "error" as const } : msg,
    );

    messagesCacheRef.current[adminId] = next;

    if (selectedUserIdRef.current === adminId) {
      setMessages(next);
    }
  };

  const sendPendingMessage = async (
    pending: PendingRrhhMessage,
    currentFile?: File | null,
  ) => {
    const clientId = pending.client_id;
    if (!clientId || sendingIdsRef.current.has(clientId)) return;

    sendingIdsRef.current.add(clientId);

    try {
      const stored = await sendRrhhChatMessage(
        pending.admin_id,
        pending.mensaje,
        clientId,
        currentFile,
      );

      delete pendingMessagesRef.current[clientId];
      persistPendingMessages();

      replacePendingInCache(pending.admin_id, clientId, stored);
      void loadUsers();
    } catch {
      markPendingError(pending.admin_id, clientId);
    } finally {
      sendingIdsRef.current.delete(clientId);
    }
  };

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();

    if (!q) return users;

    return users.filter((u) =>
      `${u.nombre} ${u.correo} ${u.rol}`.toLowerCase().includes(q),
    );
  }, [users, userSearch]);

  const filteredGroups = useMemo(() => {
    const q = userSearch.trim().toLocaleLowerCase("es");
    if (!q) return groups;
    return groups.filter((group) =>
      [group.nombre, ...group.members.map((member) => member.nombre)]
        .join(" ")
        .toLocaleLowerCase("es")
        .includes(q),
    );
  }, [groups, userSearch]);

  const orderedConversations = useMemo(
    () =>
      [
        ...filteredUsers.map((user) => ({
          kind: "user" as const,
          id: user.id,
          name: user.nombre,
          lastAt: user.ultimo_en,
          user,
        })),
        ...filteredGroups.map((group) => ({
          kind: "group" as const,
          id: group.id,
          name: group.nombre,
          lastAt: group.last_message_at,
          group,
        })),
      ].sort((a, b) => {
        const aTime = a.lastAt ? new Date(a.lastAt).getTime() : 0;
        const bTime = b.lastAt ? new Date(b.lastAt).getTime() : 0;
        if (aTime !== bTime) return bTime - aTime;
        return a.name.localeCompare(b.name, "es", { sensitivity: "base" });
      }),
    [filteredGroups, filteredUsers],
  );

  const visibleMessages = useMemo(() => {
    const q = floating ? "" : chatSearch.trim().toLowerCase();

    if (!q) return messages;

    return messages.filter((m) => m.mensaje.toLowerCase().includes(q));
  }, [messages, chatSearch, floating]);

  const loadUsers = async () => {
    setLoadingUsers(true);

    try {
      const data = await getRrhhChatUsers();

      setUsers(data);

      setSelectedUser((current) => {
        if (!current) return data[0] ?? null;

        return data.find((u) => u.id === current.id) ?? data[0] ?? null;
      });
    } catch {
      // Evita ruido en consola con red móvil.
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadMessages = async (adminId: number, force = false) => {
    if (loadingMessagesRef.current[adminId]) return;

    if (!force && messagesCacheRef.current[adminId]) {
      if (selectedUserIdRef.current === adminId) {
        setMessages(messagesCacheRef.current[adminId]);
      }

      return;
    }

    loadingMessagesRef.current[adminId] = true;
    setLoadingChatId(adminId);

    try {
      const data = await getRrhhChatMessages(adminId);

      const pending = Object.values(pendingMessagesRef.current)
        .filter((msg) => msg.admin_id === adminId)
        .map((msg) => ({
          ...msg,
          status: msg.status ?? "sending",
        }));

      const pendingNotConfirmed = pending.filter(
        (tmp) =>
          !data.some(
            (real) =>
              real.from === tmp.from &&
              real.mensaje === tmp.mensaje &&
              Math.abs(
                new Date(real.creado_en).getTime() -
                  new Date(tmp.creado_en).getTime(),
              ) < 60000,
          ),
      );

      const next = sortChatMessages([...data, ...pendingNotConfirmed]);

      messagesCacheRef.current[adminId] = next;

      if (selectedUserIdRef.current === adminId) {
        setMessages(next);
      }
    } catch {
      // Sin ruido en consola.
    } finally {
      loadingMessagesRef.current[adminId] = false;
      setLoadingChatId((current) => (current === adminId ? null : current));
    }
  };

  const openChat = (user: RrhhChatUser) => {
    selectGroup(null);
    selectedUserIdRef.current = user.id;
    setSelectedUser(user);
    setMessages(messagesCacheRef.current[user.id] ?? []);
    setChatSearch("");
    setFile(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    setUsers((current) =>
      current.map((u) => (u.id === user.id ? { ...u, unread_count: 0 } : u)),
    );

    void markRrhhChatAsRead(user.id).catch(() => {});
    void loadMessages(user.id);
  };

  const insertQuick = (text: string) => {
    setMessage(text);
  };

  const onSend = async () => {
    if (!selectedUser || (!message.trim() && !file)) return;

    const adminId = selectedUser.id;
    const text = message.trim();
    const currentFile = file;

    const clientId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`;

    const tempMessage: PendingRrhhMessage = {
      id: Date.now() * -1,
      client_id: clientId,
      client_order: nextClientOrder(),
      admin_id: adminId,
      from: "me",
      emisor_id: 0,
      mensaje: text || (currentFile ? "Archivo adjunto" : ""),
      creado_en: new Date().toISOString(),
      leido: false,
      adjunto: currentFile
        ? {
            id: Date.now() * -1,
            nombre: currentFile.name,
            mime: currentFile.type,
            size: currentFile.size,
          }
        : null,
      status: "sending",
    };

    setMessage("");
    setFile(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    pendingMessagesRef.current[clientId] = tempMessage;
    persistPendingMessages();

    addMessageToCache(adminId, tempMessage);

    setUsers((current) =>
      current.map((u) =>
        u.id === adminId
          ? {
              ...u,
              ultimo: tempMessage.mensaje,
              ultimo_en: tempMessage.creado_en,
            }
          : u,
      ),
    );

    void sendPendingMessage(tempMessage, currentFile);
  };

  useEffect(() => {
    void loadUsers();
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(RRHH_PENDING_MESSAGES_KEY);

      if (!raw) return;

      const pending = JSON.parse(raw) as PendingRrhhMessage[];

      pending.forEach((msg) => {
        if (!msg.client_id) return;

        pendingMessagesRef.current[msg.client_id] = {
          ...msg,
          status: "sending",
        };

        addMessageToCache(msg.admin_id, {
          ...msg,
          status: "sending",
        });

        void sendPendingMessage({
          ...msg,
          status: "sending",
        });
      });
    } catch {
      localStorage.removeItem(RRHH_PENDING_MESSAGES_KEY);
    }
  }, []);

  useEffect(() => {
    if (!selectedUser) return;

    selectedUserIdRef.current = selectedUser.id;
    void loadMessages(selectedUser.id);

    const timer = window.setInterval(() => {
      const currentId = selectedUserIdRef.current;

      if (currentId) {
        void loadMessages(currentId, true);
      }
    }, 15000);

    return () => window.clearInterval(timer);
  }, [selectedUser?.id]);

  useEffect(() => {
    selectedUserIdRef.current = selectedUser?.id ?? null;
  }, [selectedUser?.id]);

  useLayoutEffect(() => {
    const container = messagesContainerRef.current;

    if (!container) return;

    container.scrollTo({
      top: container.scrollHeight,
      behavior: selectedUser ? "smooth" : "auto",
    });
  }, [messages.length, selectedUser?.id]);

  useEffect(() => {
    if (floating) {
      setFloatingConversationsExpanded(false);
    }
  }, [floating]);

  const showConversationList = !floating || floatingConversationsExpanded;

  return (
    <section
      className={[
        "dashboard-chat-crisp overflow-hidden border border-[#d7e5df] bg-white shadow-[0_24px_70px_rgba(15,23,42,.09)] transition-colors duration-300 hover:border-emerald-200",
        floating ? "h-full rounded-[24px]" : "mt-6 rounded-[34px]",
      ].join(" ")}
    >
      <div
        className={[
          "grid",
          floating
            ? `h-full min-h-0 ${showConversationList ? "grid-cols-[240px_minmax(0,1fr)]" : "grid-cols-1"}`
            : "min-h-[640px] grid-cols-1 xl:grid-cols-[340px_minmax(0,1fr)]",
        ].join(" ")}
      >
        <aside
          className={[
            "min-h-0 border-r border-[#edf1f3] bg-[linear-gradient(180deg,#ffffff_0%,#f7fbf8_100%)]",
            floating ? "flex flex-col overflow-hidden" : "",
            showConversationList ? "" : "hidden",
          ].join(" ")}
        >
          <div className="border-b border-[#edf1f3] p-5">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div className="text-sm font-black text-slate-950">
                  Conversaciones
                </div>

                <div className="text-xs font-semibold text-slate-500">
                  Soporte RRHH
                </div>
              </div>

              <span className="rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-[11px] font-black text-emerald-700 shadow-[0_8px_20px_rgba(16,185,129,.07)]">
                {users.length + groups.length}
              </span>
            </div>

            <div className="flex min-w-0 items-center gap-2 rounded-2xl border border-[#dfe8e4] bg-white px-3 py-2.5 shadow-[0_8px_22px_rgba(15,23,42,.045)] transition-all duration-300 focus-within:border-emerald-300 focus-within:ring-4 focus-within:ring-emerald-100 hover:border-emerald-200 hover:shadow-[0_12px_28px_rgba(16,185,129,.07)]">
              <Search size={16} className="shrink-0 text-slate-400" />
              <input
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-slate-700 outline-none placeholder:text-slate-400"
                placeholder="Buscar administrador o grupo..."
              />
            </div>
          </div>

          <div className={`dashboard-chat-scroll overflow-y-auto p-3 ${floating ? "min-h-0 flex-1 max-h-none" : "max-h-[540px]"}`}>
            {loadingUsers && users.length === 0 && loadingGroups && groups.length === 0 ? (
              <div className="flex items-center justify-center gap-2 rounded-3xl border border-dashed border-emerald-100 bg-emerald-50/60 p-5 text-center text-sm font-bold text-slate-500">
                <Loader2 size={16} className="animate-spin text-emerald-600" />
                Cargando conversaciones...
              </div>
            ) : (
              <>
                {orderedConversations.map((conversation) => {
                  if (conversation.kind === "user") {
                    const user = conversation.user;
                    const active = selectedGroupId === null && selectedUser?.id === user.id;

                    return (
                      <button
                        key={`chat-user-${user.id}`}
                        type="button"
                        onClick={() => {
                          void openChat(user);
                        }}
                        className={[
                          "group mb-2 flex w-full items-center gap-3 rounded-[24px] border p-3 text-left transition-all duration-300",
                          active
                            ? "border-emerald-200 bg-[linear-gradient(135deg,#ecfdf5_0%,#f0fdfa_100%)] shadow-[0_14px_34px_rgba(16,185,129,.10)]"
                            : "border-transparent bg-transparent hover:border-[#dfe8e4] hover:bg-white hover:shadow-[0_12px_30px_rgba(15,23,42,.06)]",
                        ].join(" ")}
                      >
                        <span className="relative grid h-11 w-11 shrink-0 place-items-center rounded-2xl border border-emerald-100 bg-emerald-50 font-black text-emerald-700 shadow-[0_8px_20px_rgba(16,185,129,.06)] transition-all duration-300 group-hover:-rotate-3 group-hover:scale-105">
                          {initials(user.nombre)}
                          {active && (
                            <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-white bg-emerald-500 shadow-[0_0_0_4px_rgba(16,185,129,.14)]" />
                          )}
                        </span>

                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-black text-slate-950">{user.nombre}</span>
                          <span className="block truncate text-xs font-semibold text-slate-500">{user.ultimo}</span>
                        </span>

                        <span
                          className={[
                            "rounded-full border px-2 py-0.5 text-[11px] font-black",
                            user.unread_count > 0
                              ? "border-amber-200 bg-amber-50 text-amber-600"
                              : "border-slate-200 bg-slate-50 text-slate-400",
                          ].join(" ")}
                        >
                          {user.unread_count > 0 ? user.unread_count : "Chat"}
                        </span>
                      </button>
                    );
                  }

                  const group = conversation.group;
                  const active = selectedGroupId === group.id;
                  return (
                    <button
                      key={`chat-group-${group.id}`}
                      type="button"
                      onClick={() => {
                        selectGroup(group.id);
                        setChatSearch("");
                      }}
                      className={[
                        "group mb-2 flex w-full items-center gap-3 rounded-[24px] border p-3 text-left transition-all duration-300",
                        active
                          ? "border-emerald-200 bg-[linear-gradient(135deg,#ecfdf5_0%,#f0fdfa_100%)] shadow-[0_14px_34px_rgba(16,185,129,.10)]"
                          : "border-transparent bg-transparent hover:border-[#dfe8e4] hover:bg-white hover:shadow-[0_12px_30px_rgba(15,23,42,.06)]",
                      ].join(" ")}
                    >
                      <span className="relative grid h-11 w-11 shrink-0 place-items-center rounded-2xl border border-emerald-100 bg-emerald-50 font-black text-emerald-700 shadow-[0_8px_20px_rgba(16,185,129,.06)] transition-all duration-300 group-hover:-rotate-3 group-hover:scale-105">
                        {initials(group.nombre)}
                        {active && (
                          <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-white bg-emerald-500 shadow-[0_0_0_4px_rgba(16,185,129,.14)]" />
                        )}
                      </span>

                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-black text-slate-950">{group.nombre}</span>
                        <span className="block truncate text-xs font-semibold text-slate-500">{group.last_message}</span>
                      </span>

                      <span
                        className={[
                          "rounded-full border px-2 py-0.5 text-[11px] font-black",
                          group.unread_count > 0
                            ? "border-amber-200 bg-amber-50 text-amber-600"
                            : "border-slate-200 bg-slate-50 text-slate-400",
                        ].join(" ")}
                      >
                        {group.unread_count > 0 ? group.unread_count : "Grupo"}
                      </span>
                    </button>
                  );
                })}
              </>
            )}

            {groupsError && groups.length === 0 && (
              <div className="mb-2 rounded-2xl border border-amber-100 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-700">
                {groupsError}
              </div>
            )}

            {!loadingUsers && !loadingGroups && orderedConversations.length === 0 && (
              <div className="rounded-3xl border border-dashed border-emerald-100 bg-emerald-50/60 p-5 text-center text-sm font-bold text-slate-500">
                No hay conversaciones con ese nombre.
              </div>
            )}
          </div>
        </aside>

        <div className="flex min-h-0 flex-col bg-[#f8fbfc]">
          {selectedGroupId === null && (
            <>
          {floating ? (
            <div className="flex shrink-0 items-center gap-2 border-b border-[#edf1f3] bg-white px-3 py-2">
              <button
                type="button"
                onClick={() => setFloatingConversationsExpanded((current) => !current)}
                className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-emerald-100 bg-white text-emerald-700 shadow-[0_6px_16px_rgba(16,185,129,.06)] transition hover:border-emerald-200 hover:bg-emerald-50"
                title={showConversationList ? "Ocultar conversaciones" : "Mostrar conversaciones"}
                aria-label={showConversationList ? "Ocultar conversaciones" : "Mostrar conversaciones"}
              >
                {showConversationList ? <PanelLeftClose size={15} /> : <PanelLeftOpen size={15} />}
              </button>

              <span className="min-w-0 flex-1 truncate text-sm font-black text-slate-950">
                {selectedUser?.nombre ?? (loadingUsers ? "Cargando conversaciones..." : "Selecciona un administrador")}
              </span>
            </div>
          ) : (
          <div className="flex items-center justify-between gap-4 border-b border-[#edf1f3] bg-white px-6 py-5">
            <div className="flex items-center gap-3">
              <span className="grid h-12 w-12 place-items-center rounded-2xl border border-emerald-100 bg-[linear-gradient(135deg,#ecfdf5_0%,#f0fdfa_100%)] text-emerald-700 shadow-[0_14px_34px_rgba(16,185,129,.10)] transition-all duration-300 hover:-rotate-3 hover:scale-105">
                <MessageCircle size={19} />
              </span>

              <div>
                <div className="flex flex-wrap items-center gap-2 text-sm font-black text-slate-950">
                  Chat con
                  <span className="rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-emerald-700">
                    {selectedUser?.nombre ?? (loadingUsers ? "Cargando conversaciones..." : "Selecciona un administrador")}
                  </span>
                </div>

                <div className="mt-1 flex items-center gap-2 text-xs font-semibold text-slate-500">
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-400 shadow-[0_0_0_4px_rgba(52,211,153,.13)]" />
                  Disponible
                </div>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              {floating && (
                <button
                  type="button"
                  onClick={() => setFloatingConversationsExpanded((current) => !current)}
                  className="grid h-10 w-10 place-items-center rounded-xl border border-emerald-100 bg-white text-emerald-700 shadow-[0_8px_20px_rgba(16,185,129,.07)] transition hover:border-emerald-200 hover:bg-emerald-50"
                  title={showConversationList ? "Ocultar conversaciones" : "Mostrar conversaciones"}
                  aria-label={showConversationList ? "Ocultar conversaciones" : "Mostrar conversaciones"}
                >
                  {showConversationList ? <PanelLeftClose size={16} /> : <PanelLeftOpen size={16} />}
                </button>
              )}

              <div className="hidden items-center gap-2 lg:flex">
                <input
                value={chatSearch}
                onChange={(e) => setChatSearch(e.target.value)}
                className="rounded-2xl border border-[#dfe8e4] bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-[0_8px_22px_rgba(15,23,42,.045)] outline-none transition-all duration-300 placeholder:text-slate-400 focus:border-emerald-300 focus:ring-4 focus:ring-emerald-100 hover:border-emerald-200"
                placeholder="Buscar en el chat..."
              />

              <button
                type="button"
                onClick={() => setShowPinned((v) => !v)}
                className={[
                  "grid h-11 w-11 place-items-center rounded-2xl border transition-all duration-300 hover:shadow-[0_12px_28px_rgba(16,185,129,.10)]",
                  showPinned
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-[#dfe8e4] bg-white text-slate-500 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700",
                ].join(" ")}
                title="Ver/ocultar información"
              >
                <Pin size={16} />
              </button>
              </div>
            </div>
          </div>
          )}
          {!floating && showPinned && (
            <div className="flex flex-col gap-3 border-b border-[#edf1f3] bg-[linear-gradient(135deg,#ffffff,#f4fbf8)] p-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-start gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl border border-emerald-100 bg-emerald-50 text-emerald-700 shadow-[0_8px_20px_rgba(16,185,129,.08)]">
                  <Info size={17} />
                </span>

                <p className="text-sm font-semibold leading-6 text-slate-500">
                  <b className="font-black text-slate-950">Chat RRHH:</b>{" "}
                  contacta con un administrador para dudas sobre fichaje,
                  vacaciones, nóminas o documentación.
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                {pinnedReplies.map((reply) => (
                  <button
                    key={reply}
                    type="button"
                    onClick={() => insertQuick(reply)}
                    className="rounded-full border border-emerald-100 bg-white px-3 py-1.5 text-xs font-black text-emerald-700 shadow-[0_6px_16px_rgba(16,185,129,.06)] transition-all duration-300 hover:bg-emerald-50"
                  >
                    {reply}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-hidden">
            <div
              ref={messagesContainerRef}
              className={`dashboard-chat-scroll overflow-y-auto space-y-4 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,.06),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(20,184,166,.05),transparent_24%),#f8fbfc] p-6 ${floating ? "h-full" : "h-[500px]"}`}
            >
              <div className="mx-auto mb-5 w-fit rounded-full border border-emerald-100 bg-white/90 px-3 py-1 text-xs font-black text-slate-400 shadow-[0_8px_22px_rgba(15,23,42,.045)]">
                Hoy
              </div>

              {!selectedUser && loadingUsers && (
                <div className="flex min-h-[300px] items-center justify-center gap-2 text-sm font-bold text-slate-400">
                  <Loader2 size={17} className="animate-spin text-emerald-600" />
                  Cargando chat...
                </div>
              )}

              {!selectedUser && !loadingUsers && (
                <div className="flex min-h-[300px] items-center justify-center text-sm font-bold text-slate-400">
                  Selecciona un administrador para abrir el chat.
                </div>
              )}

              {selectedUser && chatSearch && visibleMessages.length === 0 && (
                <div className="flex min-h-[220px] items-center justify-center text-sm font-bold text-slate-400">
                  No hay mensajes que coincidan con la búsqueda.
                </div>
              )}

              {selectedUser &&
                loadingChatId === selectedUser.id &&
                selectedUser.ultimo !== "Sin mensajes todavía" &&
                visibleMessages.length === 0 && (
                  <div className="mx-auto w-fit rounded-full border border-emerald-100 bg-white px-3 py-1 text-xs font-black text-slate-400 shadow-[0_8px_22px_rgba(15,23,42,.045)]">
                    Cargando mensajes...
                  </div>
                )}

              {visibleMessages.map((msg) => {
                const isMe = msg.from === "me";
                const attachment = msg.adjunto;
                const isImageAttachment = attachment?.mime?.startsWith("image/") ?? false;

                return (
                  <div
                    key={msg.id}
                    className={[
                      "flex animate-slide-up",
                      isMe ? "justify-end" : "justify-start",
                    ].join(" ")}
                  >
                    <div
                      className={[
                        "max-w-[76%] rounded-[24px] px-4 py-3 text-sm font-semibold leading-6 shadow-[0_10px_28px_rgba(15,23,42,.05)] transition-colors duration-300",
                        isMe
                          ? "border border-emerald-200 bg-[linear-gradient(135deg,#10b981,#14b8a6)] text-white shadow-[0_14px_34px_rgba(16,185,129,.16)]"
                          : "border border-[#e7ecef] bg-white text-slate-700",
                      ].join(" ")}
                    >
                      <div>{msg.mensaje}</div>

                      {attachment && (
                        <div
                          className={[
                            "mt-3 w-full max-w-[360px] rounded-2xl border px-3 py-3",
                            isMe
                              ? "border-white/20 bg-white/15 text-white"
                              : "border-emerald-100 bg-emerald-50/70 text-slate-600",
                          ].join(" ")}
                        >
                          <div className="flex items-center gap-3">
                            <span
                              className={[
                                "grid h-10 w-10 shrink-0 place-items-center rounded-xl",
                                isMe
                                  ? "bg-white/15"
                                  : "bg-white text-emerald-700",
                              ].join(" ")}
                            >
                              {isImageAttachment ? (
                                <ImageIcon size={17} />
                              ) : (
                                <FileText size={17} />
                              )}
                            </span>

                            <span className="min-w-0 flex-1">
                              <span
                                className="block truncate text-sm font-black"
                                title={attachment.nombre}
                              >
                                {attachment.nombre}
                              </span>

                              <span
                                className={[
                                  "text-[11px] font-bold",
                                  isMe ? "text-white/65" : "text-slate-400",
                                ].join(" ")}
                              >
                                {formatFileSize(attachment.size)}
                              </span>
                            </span>
                          </div>

                          {attachment.id > 0 ? (
                            <div className="mt-2 flex items-center gap-2">
                              {isImageAttachment && (
                                <button
                                  type="button"
                                  onClick={() => setPreviewAttachment(attachment)}
                                  className={[
                                    "inline-flex items-center gap-1.5 rounded-xl border px-2.5 py-1.5 text-[11px] font-black transition",
                                    isMe
                                      ? "border-white/20 bg-white/10 text-white hover:bg-white/20"
                                      : "border-emerald-100 bg-white text-emerald-700 hover:bg-emerald-50",
                                  ].join(" ")}
                                >
                                  <Eye size={13} />
                                  Ver
                                </button>
                              )}

                              <button
                                type="button"
                                onClick={() => void downloadRrhhChatAttachment(attachment.id)}
                                className={[
                                  "inline-flex items-center gap-1.5 rounded-xl border px-2.5 py-1.5 text-[11px] font-black transition",
                                  isMe
                                    ? "border-white/20 bg-white/10 text-white hover:bg-white/20"
                                    : "border-emerald-100 bg-white text-emerald-700 hover:bg-emerald-50",
                                ].join(" ")}
                              >
                                <Download size={13} />
                                Descargar
                              </button>
                            </div>
                          ) : (
                            <div className={[
                              "mt-2 text-[11px] font-bold",
                              isMe ? "text-white/65" : "text-slate-400",
                            ].join(" ")}>
                              Enviando archivo...
                            </div>
                          )}
                        </div>
                      )}

                      <div
                        className={[
                          "mt-1 text-[10px] font-bold",
                          isMe ? "text-white/65" : "text-slate-400",
                        ].join(" ")}
                      >
                        {formatTime(msg.creado_en)}

                        {msg.status === "sending" && isMe && (
                          <div className="mt-0.5 text-[10px] font-bold text-white/55">
                            Enviando...
                          </div>
                        )}

                        {msg.status === "error" && isMe && (
                          <div className="mt-0.5 text-[10px] font-bold text-rose-100">
                            Pendiente de reintento
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              <div ref={messagesEndRef} />
            </div>
          </div>

          <div className="border-t border-[#edf1f3] bg-white p-5">
            {!floating && (
              <div className="mb-3 flex flex-wrap gap-2">
              {quickReplies.map((reply) => (
                <button
                  key={reply}
                  type="button"
                  onClick={() => insertQuick(reply)}
                  className="rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1.5 text-xs font-black text-emerald-700 shadow-[0_6px_16px_rgba(16,185,129,.06)] transition-colors duration-300 hover:bg-emerald-100"
                >
                  {reply}
                </button>
              ))}
            </div>
            )}

            {file && (
              <div className="mb-3 flex items-center justify-between rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-bold text-slate-700 shadow-[0_8px_20px_rgba(16,185,129,.06)]">
                <span className="truncate">Adjunto: {file.name}</span>

                <button
                  type="button"
                  onClick={() => setFile(null)}
                  className="grid h-7 w-7 place-items-center rounded-full bg-white text-slate-500 shadow-[0_6px_14px_rgba(15,23,42,.05)] transition-colors duration-300 hover:text-rose-500"
                >
                  <X size={14} />
                </button>
              </div>
            )}

            <div className="flex items-center gap-3 rounded-[28px] border border-[#dfe8e4] bg-white p-3 shadow-[0_18px_44px_rgba(15,23,42,.08)] transition-all duration-300 focus-within:border-emerald-300 focus-within:ring-4 focus-within:ring-emerald-100">
              <label
                className="grid h-11 w-11 shrink-0 cursor-pointer place-items-center rounded-2xl border border-emerald-100 bg-emerald-50 text-emerald-700 transition-colors duration-300 hover:bg-emerald-100"
                title="Adjuntar archivo"
              >
                <Paperclip size={17} />

                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
              </label>

              <input
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void onSend();
                  }
                }}
                className="min-w-0 flex-1 bg-transparent px-2 text-sm font-semibold text-slate-700 outline-none placeholder:text-slate-400"
                placeholder="Escribe un mensaje..."
              />

              <button
                type="button"
                onClick={() => void onSend()}
                disabled={!selectedUser || (!message.trim() && !file)}
                className="inline-flex shrink-0 items-center gap-2 rounded-2xl bg-[linear-gradient(135deg,#10b981,#14b8a6)] px-5 py-3 text-sm font-black text-white shadow-[0_18px_40px_rgba(16,185,129,.24)] transition-colors duration-300 hover:shadow-[0_24px_52px_rgba(16,185,129,.30)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Send size={16} />
                Enviar
              </button>
            </div>
          </div>
            </>
          )}

          <GroupChatWorkspace
            scope="rrhh"
            floating={floating}
            active={selectedGroupId !== null}
            showConversationList={showConversationList}
            onToggleConversationList={() =>
              setFloatingConversationsExpanded((current) => !current)
            }
          />
        </div>
      </div>
      <ChatImagePreviewModal
        attachmentId={previewAttachment?.id ?? null}
        filename={previewAttachment?.nombre ?? "Imagen adjunta"}
        loadImage={loadRrhhChatAttachmentPreview}
        onClose={() => setPreviewAttachment(null)}
      />
    </section>
  );
}

function initials(name: string) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

function formatTime(value: string) {
  return new Date(value).toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatFileSize(size?: number | null) {
  if (!size) return "Archivo";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
}
